import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const useSocket = (userId) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Initialize socket connection
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      setIsConnected(true);

      // Join user room if authenticated
      if (userId) {
        socket.emit('join', userId);
      }
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
    });

    // Notification events
    socket.on('notification', (data) => {
      console.log('New notification:', data);
      setNotifications((prev) => [data, ...prev]);
      setUnreadCount((prev) => prev + 1);

      // Show toast notification
      toast.custom((t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-2xl rounded-2xl pointer-events-auto flex ring-1 ring-black ring-opacity-5 overflow-hidden`}
        >
          <div className={`w-1.5 ${
            data.type === 'success' ? 'bg-green-500' :
            data.type === 'error' ? 'bg-red-500' :
            data.type === 'warning' ? 'bg-yellow-500' :
            'bg-primary-500'
          }`} />
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{data.title}</p>
                <p className="mt-1 text-sm text-gray-500">{data.message}</p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-200">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-primary-600 hover:text-primary-500 focus:outline-none"
            >
              Close
            </button>
          </div>
        </div>
      ), { duration: 5000 });
    });

    // Order update events
    socket.on('order_update', (data) => {
      console.log('Order update:', data);
      toast.success(data.message, { duration: 5000 });
    });

    // Cart sync events (for multi-device sync)
    socket.on('cart_sync', (cartItems) => {
      console.log('Cart sync received:', cartItems);
      // Update local cart state
    });

    // Broadcast events
    socket.on('broadcast', (data) => {
      console.log('Broadcast:', data);
      toast.info(data.message, { duration: 5000 });
    });

    // Chat message events
    socket.on('new_message', (data) => {
      console.log('New message:', data);
      // Handle incoming chat message
    });

    // Typing indicator
    socket.on('typing', (data) => {
      // Handle typing indicator
    });

    return () => {
      socket.disconnect();
    };
  }, [userId]);

  // Emit functions
  const emit = useCallback((event, data) => {
    if (socketRef.current) {
      socketRef.current.emit(event, data);
    }
  }, []);

  const joinRoom = useCallback((room) => {
    if (socketRef.current) {
      socketRef.current.emit('join', room);
    }
  }, []);

  const trackOrder = useCallback((orderId) => {
    if (socketRef.current) {
      socketRef.current.emit('track_order', orderId);
    }
  }, []);

  const sendMessage = useCallback((recipientId, message) => {
    if (socketRef.current) {
      socketRef.current.emit('send_message', { recipientId, message, senderId: userId });
    }
  }, [userId]);

  const markNotificationsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  return {
    socket: socketRef.current,
    isConnected,
    notifications,
    unreadCount,
    emit,
    joinRoom,
    trackOrder,
    sendMessage,
    markNotificationsRead,
  };
};

// Hook for real-time order tracking
export const useOrderTracking = (orderId) => {
  const { socket, isConnected, trackOrder } = useSocket();
  const [status, setStatus] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);

  useEffect(() => {
    if (orderId && socket) {
      trackOrder(orderId);

      socket.on('status_change', (data) => {
        setStatus(data.status);
        setStatusHistory((prev) => [...prev, { status: data.status, message: data.message, timestamp: new Date().toISOString() }]);
      });
    }

    return () => {
      if (socket) {
        socket.off('status_change');
      }
    };
  }, [orderId, socket, trackOrder]);

  return { status, statusHistory, isConnected };
};

// Hook for real-time chat
export const useChat = (recipientId) => {
  const { socket, sendMessage } = useSocket();
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (socket) {
      socket.on('new_message', (data) => {
        if (data.senderId === recipientId) {
          setMessages((prev) => [...prev, data]);
        }
      });

      socket.on('typing', (data) => {
        if (data.senderId === recipientId) {
          setIsTyping(data.isTyping);
        }
      });
    }

    return () => {
      if (socket) {
        socket.off('new_message');
        socket.off('typing');
      }
    };
  }, [socket, recipientId]);

  const send = useCallback((message) => {
    sendMessage(recipientId, message);
    setMessages((prev) => [...prev, { senderId: 'me', message, timestamp: new Date().toISOString() }]);
  }, [recipientId, sendMessage]);

  const setTyping = useCallback((isTyping) => {
    if (socket) {
      socket.emit('typing', { recipientId, isTyping });
    }
  }, [socket, recipientId]);

  return { messages, send, isTyping, setTyping };
};
